/**
 * Helpers to get the svg code of an icon that is present inside the svg sprite we generate
 */

function isStyleNode(element: Element): element is HTMLStyleElement {
  return element.tagName === 'style'
}

function isNodeElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE
}

/**
 * Sanitize specific styles that are known to be safe. Avoid XSS
 */
function sanitizeStyles(styles: string) {
  // Allow specific styles that are known to be safe
  // and remove potentially harmful ones
  styles = styles.replace(/(^|[^-])font-family:/g, '$1')
  styles = styles.replace(/(^|[^-])fill:/g, '$1')
  styles = styles.replace(/(^|[^-])stroke:/g, '$1')
  styles = styles.replace(/(^|[^-])stroke-width:/g, '$1')
  return styles
}

/**
 * Sanitize svg code. Avoid XSS
 */
function sanitize(node: Node) {
  if (isNodeElement(node)) {
    // Remove any elements that are not allowed
    if (node.tagName === 'script') {
      node.remove()
    }
    // Remove any attributes that are not allowed
    for (let i = 0; i < node.attributes.length; i++) {
      const attr = node.attributes[i]
      if (attr.name.startsWith('on')) {
        node.removeAttribute(attr.name)
      }
    }
    // Sanitize specific styles that are known to be safe
    if (isStyleNode(node)) {
      node.textContent = sanitizeStyles(node.textContent || '')
    } else {
      // Remove any styles that are not allowed
      const styles = node.getAttribute('style')
      if (styles) {
        const sanitizedStyles = sanitizeStyles(styles)
        if (sanitizedStyles) {
          node.setAttribute('style', sanitizedStyles)
        } else {
          node.removeAttribute('style')
        }
      }
    }
    // Recursively sanitize child nodes
    for (let i = 0; i < node.childNodes.length; i++) {
      sanitize(node.childNodes[i])
    }
  }
}

/**
 * Get the SVG code for a particular icon in the sprite
 * @param iconName the name of the icon in the sprite (eg: asset spriteLogo)
 */
export function getIconSvg(iconName: string) {
  const symbol = document.getElementById(iconName)
  if (!symbol) {
    return ''
  }
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  const symbolAttributes = symbol.attributes
  for (let i = 0; i < symbolAttributes.length; i++) {
    svg.setAttribute(symbolAttributes[i].name, symbolAttributes[i].value)
  }
  const symbolChildren = Array.from(symbol.children)
  for (const child of symbolChildren) {
    svg.appendChild(child.cloneNode(true))
  }
  sanitize(svg)
  return svg.outerHTML
}
